"use client";

// React imports
import { useMounted } from "@/hooks/useMounted";

// Sanity.io imports
import { PortableText } from "next-sanity";

// Hooks, Utility & Helper function imports
import { createPortableTextComponents } from "@/utils/portableTextComponents";
import classNames from "@/utils/classNames";
import formatImageUrl from "@/utils/formatImageUrl";
import NextLink from "@/components/interactive/NextLink";
import isovTheme from "@/utils/theme";
import NextImage from "@/components/interactive/NextImage";
import Video from "@/components/interactive/Video";
import { t } from "@/utils/t";

import {
  IsovTextStack,
  IsovButton,
  IsovMediaPlaceholder,
} from "@/components/stencil";

interface TextStackProps {
  theme?: string;
  variant?: string;
  logo?: string;
  icon?: any;
  stepper?: string;
  eyebrow?: any;
  headline?: any;
  headlineSize?: any;
  subtext?: string;
  text?: any;
  ctas?: any;
  image?: string;
  graphicImage?: any;
  alt?: any;
  locale?: string;
  alignment?: string;
  className?: string;
  rounded?: boolean;
  decorated?: boolean;
  shaded?: boolean;
  restricted?: boolean;
  skeleton?: boolean;
}

export default function TextStack({
  theme,
  variant,
  logo,
  icon,
  stepper,
  eyebrow,
  headline,
  headlineSize,
  subtext,
  text,
  ctas,
  image,
  graphicImage,
  alt,
  locale = "en",
  alignment,
  className,
  rounded,
  decorated,
  shaded,
  restricted,
  skeleton,
}: TextStackProps) {
  const ptComponents = createPortableTextComponents(locale);
  const componentTheme = isovTheme(theme);
  const mounted = useMounted();
  if (!mounted) return <div className="min-h-32" aria-hidden="true" />;

  return (
    <>
      <IsovTextStack
        theme={theme}
        variant={variant}
        icon={icon}
        stepper={stepper}
        eyebrow={t(eyebrow, locale)}
        headline={t(headline, locale)}
        headlineSize={headlineSize}
        subtext={subtext}
        alignment={alignment}
        restricted={restricted}
        skeleton={skeleton}
        className={className}
      >
        {logo && (
          <div
            slot="logo"
            className={classNames(
              "w-auto h-10 mb-4 overflow-hidden object-contain",
              alignment === "left" &&
                "ml-0 justify-items-start justify-start text-left",
              alignment === "center" &&
                "ml-0 sm:mx-auto justify-center justify-items-center text-center",
              alignment === "right" &&
                "ml-0 sm:ml-auto sm:mr-0 justify-items-end justify-end text-right",
            )}
          >
            <NextImage
              src={logo}
              alt={logo || "Image"}
              className="h-full w-full object-contain"
              maxWidth={300}
              width={300}
              height={110}
              sizes="(max-width: 640px) 50vw, 300px"
              isOptimized
              priority
            />
          </div>
        )}

        {text && (
          <>
            {typeof text === "string" ? (
              text
            ) : (
              <PortableText
                value={t(text, locale)}
                components={ptComponents}
                onMissingComponent={(message, options) => {
                  console.warn(message, {
                    type: options.type,
                    nodeType: options.nodeType,
                  });
                }}
              />
            )}
          </>
        )}

        {ctas && (
          <div
            className={classNames(
              "inline-flex flex-wrap gap-6 items-center content-center ml-0 mr-auto",
              alignment === "left" && "sm:ml-0 sm:mr-auto",
              alignment === "center" && "sm:mx-auto",
              alignment === "right" && "sm:ml-auto sm:mr-0",
            )}
            slot="cta"
          >
            {ctas?.map((cta: any) => (
              <NextLink
                key={`cta-${cta?._key}-${t(cta?.label, locale)}`}
                className="transition-item"
                href={cta?.url}
                target={cta?.openInNewTab ? "_blank" : undefined}
                type={`button_${cta?.variant ?? "default"}`}
              >
                <IsovButton
                  theme={
                    theme === "dark" && cta?.variant === "primary"
                      ? "default"
                      : theme
                  }
                  variant={cta?.variant}
                  label={t(cta?.label, locale)}
                  // emboss={theme === "dark"}
                  skeleton={skeleton}
                />
              </NextLink>
            ))}
          </div>
        )}
      </IsovTextStack>

      {image && (
        <IsovMediaPlaceholder theme={theme} type="title-text">
          <NextImage
            slot="media"
            src={formatImageUrl(image, alt)}
            alt={alt || "Image"}
            className={classNames(
              "transition-item h-full w-full object-contain mt-3 -mb-10",
              rounded && "rounded-large overflow-hidden",
              decorated &&
                `decorated outline-1 outline-offset-8 outline-${componentTheme.outline} outline-dashed`,
              shaded && `shaded theme-${theme}`,
              (decorated || shaded) &&
                `bg-${theme === "dark" ? componentTheme?.background : `${componentTheme?.backgroundHero}/30`} border border-${componentTheme.border}`,
            )}
            maxWidth={1600}
            width={1600}
            height={1600}
            sizes="(max-width: 768px) 100vw, 50vw"
            isOptimized
            priority
          />
        </IsovMediaPlaceholder>
      )}

      {graphicImage?.videoId && (
        <div slot="media" className="w-full h-full relative grid">
          <Video
            theme={theme}
            videoId={t(graphicImage?.videoId, locale)}
            playlistId={t(graphicImage?.playlistId, locale)}
            startAt={t(graphicImage?.startAt, locale)}
            title={`${t(headline, locale)} - Video`}
            className={classNames(
              "transition-item h-full w-full object-contain -mb-10",
              rounded && "rounded-large overflow-hidden",
              decorated &&
                `decorated outline-1 outline-offset-8 outline-${componentTheme.outline} outline-dashed`,
              shaded && `shaded theme-${theme}`,
              (decorated || shaded) &&
                `bg-${theme === "dark" ? componentTheme?.background : `${componentTheme?.backgroundHero}/30`} border border-${componentTheme.border}`,
            )}
          />
        </div>
      )}
    </>
  );
}
